import { ref, computed, type Ref } from 'vue';
import { adminService } from '@/services/adminService';
import { subscriptionService } from '@/services/subscriptionService';

interface Restaurant {
  id: number;
  name: string;
  subdomain: string;
  subscription_id?: number;
  plan_id?: number;
  plan_name?: string;
  subscription_status?: string;
  current_price?: number;
  monthly_price?: number;
  annual_price?: number;
  billing_cycle?: string;
  days_until_renewal?: number;
}

interface AdminForm {
  full_name: string;
  email: string;
  password: string;
}

export function useManageSubscriptionModal(restaurant: Ref<Restaurant>) {
  // State
  const loading = ref(false);
  const plans = ref<any[]>([]);
  const selectedPlanId = ref<number | null>(null);
  const billingCycle = ref<'monthly' | 'annual'>('monthly');
  const subscriptionDetails = ref<any>(null);

  // Admin users state
  const loadingAdmins = ref(false);
  const admins = ref<any[]>([]);
  const showCreateAdminForm = ref(false);
  const creatingAdmin = ref(false);
  const adminForm = ref<AdminForm>({
    full_name: '',
    email: '',
    password: ''
  });

  // Computed
  const hasSubscription = computed(() => !!restaurant.value.subscription_id);
  const selectedPlan = computed(() => plans.value?.find(p => p.id === selectedPlanId.value));
  const hasAdmins = computed(() => admins.value.length > 0);

  // Methods
  const loadPlans = async () => {
    try {
      plans.value = await subscriptionService.getPlans();
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const loadSubscriptionDetails = async () => {
    if (!hasSubscription.value) return;
    
    try {
      subscriptionDetails.value = await adminService.getRestaurantSubscription(restaurant.value.id);
    } catch (error) {
      console.error('Error loading subscription details:', error);
    }
  };

  const loadAdmins = async () => {
    loadingAdmins.value = true;
    try {
      const response = await adminService.getRestaurantAdmins(restaurant.value.id);
      admins.value = response.admins || [];
    } catch (error) {
      console.error('Error loading admins:', error);
      admins.value = [];
    } finally {
      loadingAdmins.value = false;
    }
  };

  const handleCreateAdmin = async () => {
    if (!adminForm.value.full_name || !adminForm.value.email || !adminForm.value.password) {
      alert('Por favor completa todos los campos');
      return;
    }
    
    creatingAdmin.value = true;
    try {
      await adminService.createRestaurantAdmin(restaurant.value.id, adminForm.value);
      
      // Reload admins list
      await loadAdmins();
      
      // Reset form
      resetAdminForm();
      
      alert('Admin creado exitosamente');
    } catch (error: any) {
      console.error('Error creating admin:', error);
      
      // Handle validation errors
      if (error.response?.data?.error?.validation_errors) {
        const validationErrors = error.response.data.error.validation_errors;
        const errorMessages = validationErrors.map((err: any) => err.message).join('\n');
        alert(`Error de validación:\n${errorMessages}`);
      } else {
        alert(error.response?.data?.error?.message || error.response?.data?.detail || 'Error al crear el admin');
      }
    } finally {
      creatingAdmin.value = false;
    }
  };

  const resetAdminForm = () => {
    adminForm.value = {
      full_name: '',
      email: '',
      password: ''
    };
    showCreateAdminForm.value = false;
  };

  const saveSubscription = async () => {
    if (!selectedPlanId.value) return;
    
    loading.value = true;
    try {
      if (hasSubscription.value) {
        // Upgrade/change plan
        await adminService.upgradeSubscription(restaurant.value.id, {
          new_plan_id: selectedPlanId.value
        });
      } else {
        // Create new subscription
        await adminService.createSubscription(restaurant.value.id, {
          plan_id: selectedPlanId.value,
          billing_cycle: billingCycle.value
        });
      }
      
      return true; // Success
    } catch (error: any) {
      console.error('Error saving subscription:', error);
      alert(error.response?.data?.detail || 'Error saving subscription');
      return false; // Failure
    } finally {
      loading.value = false;
    }
  };

  const cancelSubscription = async (immediate: boolean) => {
    const confirmMsg = immediate
      ? '¿Estás seguro de que quieres cancelar la suscripción inmediatamente?'
      : '¿Estás seguro de que quieres cancelar la suscripción al final del período?';
    
    if (!confirm(confirmMsg)) return false;
    
    loading.value = true;
    try {
      await adminService.cancelSubscription(restaurant.value.id, immediate);
      return true; // Success
    } catch (error: any) {
      console.error('Error cancelling subscription:', error);
      alert(error.response?.data?.detail || 'Error cancelling subscription');
      return false; // Failure
    } finally {
      loading.value = false;
    }
  };

  const getStatusClass = (status: string) => {
    const classes: Record<string, string> = {
      'trial': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'past_due': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'expired': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  };

  const initializeModal = async () => {
    await loadPlans();
    await loadSubscriptionDetails();
    await loadAdmins();
    
    // Pre-select current plan if exists
    if (hasSubscription.value && restaurant.value.plan_id) {
      selectedPlanId.value = restaurant.value.plan_id;
    }
  };

  return {
    // State
    loading,
    plans,
    selectedPlanId,
    billingCycle,
    subscriptionDetails,
    loadingAdmins,
    admins,
    showCreateAdminForm,
    creatingAdmin,
    adminForm,
    
    // Computed
    hasSubscription,
    selectedPlan,
    hasAdmins,
    
    // Methods
    loadPlans,
    loadSubscriptionDetails,
    loadAdmins,
    handleCreateAdmin,
    resetAdminForm,
    saveSubscription,
    cancelSubscription,
    getStatusClass,
    initializeModal
  };
}
